import { CloseCircleFilled } from '@ant-design/icons';
import {
  type ForwardedRef,
  forwardRef,
  type ReactElement,
  type ReactNode,
  type RefAttributes,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled, { css } from 'styled-components';

import Spinner from '@/components/Common/Spinner';
import Transition from '@/components/Common/Transition';
import TreeModal from '@/components/Select/TreeModal';
import treeSelectUtils from '@/components/Select/treeSelectUtils';
import type { TreeSelectItemBase } from '@/components/Select/types';
import Typo from '@/components/Typography/Typo';
import useFetch from '@/hooks/pure/useFetch';
import { getAlphaColor } from '@/styles/colors/color.utils';
import { radius } from '@/styles/sizes';
import { customScrollbar } from '@/styles/style';

import { CenterFlex, Flex } from '../base';

export type TreeSelectInterface<T extends {}> = {
  onSelectAll: () => void;
  onDeselectAll: () => void;
  dataList: TreeSelectItemBase<T>[];
  labels: LabelT[];
  onValueChange: (labels: LabelT[]) => void;
  close: () => void;
};

type Props<T extends {}> = {
  initialOpen?: boolean;
  mode?: 'single' | 'multiple';
  style?: React.CSSProperties;
  placeholder?: string;
  values: StringNumberValue[];
  searchable?: boolean;
  header?: (params: TreeSelectInterface<T>) => ReactNode;
  footer?: (params: TreeSelectInterface<T>) => ReactNode;
  parser?: (
    dataList: TreeSelectItemBase<T>[],
    values: StringNumberValue[],
  ) => { key: StringNumberValue; node: ReactNode }[];
  fetcher: (searchValue?: string) => Promise<TreeSelectItemBase<T>[]>;
  onClear?: () => void;
  validate?: (values: StringNumberValue[]) => boolean;
  onChange: (values: StringNumberValue[]) => void;
  loading?: boolean;
  disabled?: boolean;
};

export type LabelT = {
  key: StringNumberValue;
  node: ReactNode;
};

const TreeSelect = <T extends {}>(
  props: Props<T>,
  ref: ForwardedRef<HTMLDivElement>,
) => {
  const triggerRef = useRef(null);

  const [initialized, setInitialized] = useState(false);
  const [labels, setLabels] = useState<LabelT[]>([]);
  const [selectOpen, setSelectOpen] = useState(props.initialOpen || false);
  const [searchValue, setSearchValue] = useState('');
  const [dataList, setDataList] = useState<TreeSelectItemBase<T>[] | null>(
    null,
  );
  const [filteredList, setFilteredList] = useState<
    TreeSelectItemBase<T>[] | null
  >(null);

  const fetchData = useCallback(async () => {
    if (searchValue.trim() !== '') {
      const data = await props.fetcher(searchValue);
      setFilteredList(data);
    } else {
      const data = await props.fetcher();
      setFilteredList(null);
      setDataList(data);
    }
  }, [props.fetcher, searchValue]);

  const isFetching = useFetch(fetchData);

  const loading = props.loading || isFetching;

  const onValueChange = (values: LabelT[]) => {
    setLabels(values);
    props.onChange(values.map((v) => v.key));
  };

  const removeItem = (value: StringNumberValue) => {
    onValueChange(labels.filter((v) => v.key !== value));
  };

  const onCheck = ({
    value,
    exceptSelf,
    exceptChild,
    action,
  }: {
    value: StringNumberValue;
    exceptSelf?: boolean;
    exceptChild?: boolean;
    action?: 'add' | 'remove';
  }) => {
    const compositedList = [...(dataList || []), ...(filteredList || [])];
    const target = treeSelectUtils.findNode(compositedList, value);
    if (!target) return props.values;

    if (props.mode === 'single') {
      if (action === 'remove') {
        onValueChange([]);
        return;
      }
      onValueChange([
        {
          key: value,
          node: target.node,
        },
      ]);
      return;
    }

    const childs = treeSelectUtils
      .getFlattenChildNodes(target.childNodes || [])
      .filter((data) => !data.checkOption?.disable);

    const newValues = [...props.values];
    const newLabels = [...labels];

    const toggleItem = (
      item: TreeSelectItemBase,
      action?: 'add' | 'remove',
      exceptSelf?: boolean,

      // action이 지정되지 않은 경우 toggle을 기본동작으로 실행함
    ) => {
      const valueIndex = newValues.findIndex((value) => value === item.alias);
      const labelIndex = newLabels.findIndex(
        (label) => label.key === item.alias,
      );
      if (item.checkOption?.disable) return valueIndex;
      if (exceptSelf) return valueIndex;
      switch (action) {
        case 'add': {
          if (valueIndex === -1) {
            newValues.push(item.alias);
          }
          if (labelIndex === -1) {
            newLabels.push({
              key: item.alias,
              node: item.node,
            });
          }
          break;
        }

        case 'remove': {
          if (valueIndex !== -1) {
            newValues.splice(valueIndex, 1);
          }
          if (labelIndex !== -1) {
            newLabels.splice(labelIndex, 1);
          }
          break;
        }

        default: {
          newValues.toggle(item.alias);
          newLabels.toggle(
            {
              key: item.alias,
              node: item.node,
            },
            (el) => el.key === item.alias,
          );
          break;
        }
      }
      return valueIndex;
    };

    const targetIndex = toggleItem(target, action, exceptSelf);

    if (!exceptChild) {
      for (const t of childs) {
        if (action) {
          toggleItem(t, action);
        } else {
          if (targetIndex === -1) {
            toggleItem(t, 'remove');
            toggleItem(t, 'add');
          } else {
            toggleItem(t, 'remove');
          }
        }
      }
    }

    if (props.validate && !props.validate(newValues)) return props.values;
    onValueChange(newLabels);
  };

  const onDeselectAll = () => {
    onValueChange([]);
  };

  const onSelectAll = () => {
    if (filteredList) {
      const children = treeSelectUtils
        .getFlattenChildNodes(filteredList)
        .filter((data) => !data.checkOption?.disable);

      const newValues = children.map((data) => data.alias);
      if (props.validate && !props.validate(newValues)) return props.values;
      onValueChange(
        children.map((data) => ({
          key: data.alias,
          node: data.node,
        })),
      );

      return;
    }
    if (!dataList) return;
    const children = treeSelectUtils
      .getFlattenChildNodes(dataList)
      .filter((data) => !data.checkOption?.disable);
    const newValues = children.map((data) => data.alias);
    if (props.validate && !props.validate(newValues)) return props.values;

    onValueChange(
      children.map((data) => ({
        key: data.alias,
        node: data.node,
      })),
    );
  };

  const updateParentChildNode = (
    alias: StringNumberValue,
    items: TreeSelectItemBase<T>[],
  ) => {
    setDataList((prev) => {
      if (!prev) return prev;
      const newDataList = [...prev];
      const targetIndex = newDataList.findIndex((d) => d.alias === alias);
      if (targetIndex === -1) return prev;
      newDataList[targetIndex].childNodes = [...items];
      return newDataList;
    });
  };

  useEffect(() => {
    if (!dataList) return;
    if (initialized) return;
    if (props.parser) {
      const result = props.parser?.(dataList, props.values) || [];
      setLabels(result);
    } else {
      const result = props.values.reduce((acc, value) => {
        const data = treeSelectUtils.findNode(dataList, value);
        if (!data) return acc;
        acc.push({
          key: data.alias,
          node: data.node,
        });
        return acc;
      }, [] as LabelT[]);
      setLabels(result);
    }
    setInitialized(true);
  }, [dataList, props, initialized]);

  const displayData = useMemo(() => {
    if (searchValue) {
      return filteredList || [];
    }
    return dataList || [];
  }, [dataList, filteredList, searchValue]);

  const closeModal = () => {
    setSearchValue('');
    setSelectOpen(false);
  };

  return (
    <Style ref={ref} style={props.style} disabled={props.disabled}>
      <SelectStyle
        ref={triggerRef}
        a="center"
        onClick={!props.disabled ? () => setSelectOpen(true) : undefined}
      >
        <Flex className="selected-items" a="center">
          {labels.map((label) => (
            <SelectedItemStyle key={label.key}>
              {label.node}
              <CloseCircleFilled
                style={{ opacity: 0.4 }}
                onClick={(e) => {
                  if (props.disabled) return;
                  e.stopPropagation();
                  removeItem(label.key);
                }}
              />
            </SelectedItemStyle>
          ))}
          {labels.length === 0 && (
            <Typo type="label3" ellipsis className="txt-value text-gray-500">
              {props.placeholder || 'Select values'}
            </Typo>
          )}
        </Flex>
        {props.onClear && (
          <CenterFlex className="absolute right-1 w-6 aspect-square">
            <CloseCircleFilled
              className="ic-clear"
              onClick={(e) => {
                if (props.disabled) return;
                e.stopPropagation();
                onDeselectAll();
                props.onClear?.();
              }}
            />
          </CenterFlex>
        )}

        <Transition
          status={loading}
          css={css`
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: 10;
            display: flex;
            justify-content: center;
            align-items: center;
            background-color: ${(props) => props.theme.colors.NL80};
            border-radius: ${radius.token.sm}px;
          `}
        >
          <Spinner />
        </Transition>
      </SelectStyle>
      <TreeModal
        mode={props.mode}
        status={selectOpen}
        dataList={displayData}
        updateParentChildNode={updateParentChildNode}
        searchable={props.searchable}
        values={props.values}
        onCheck={onCheck}
        onSearchChange={setSearchValue}
        close={closeModal}
        triggerEl={triggerRef?.current}
        loading={isFetching}
        header={props.header?.({
          onSelectAll,
          onDeselectAll,
          dataList: displayData,
          labels,
          onValueChange,
          close: closeModal,
        })}
        footer={props.footer?.({
          onSelectAll,
          onDeselectAll,
          dataList: displayData,
          labels,
          onValueChange,
          close: closeModal,
        })}
        modalOptions={{
          offset: {
            y: 8,
          },
        }}
      />
    </Style>
  );
};

type TreeSelectComponentType = <T extends {}>(
  props: Props<T> & RefAttributes<HTMLDivElement>,
) => ReactElement | null;

export default forwardRef(TreeSelect) as TreeSelectComponentType;

const Style = styled.div<{ disabled?: boolean }>`
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
`;

const SelectStyle = styled(Flex)`
  position: relative;
  .selected-items {
    position: relative;
    width: 100%;
    padding: 8px 0px;
    padding-left: 12px;
    padding-right: 32px;
    flex-wrap: wrap;
    border: 1px solid ${(props) => props.theme.colors.gray400};
    min-height: 32px;
    border-radius: 6px;
    background-color: ${(props) => props.theme.colors.gray100};
    transition: all 0.2s;
    align-self: center;
    gap: 4px;
    max-height: 160px;
    overflow: auto;
    ${customScrollbar};
    &:hover {
      border: 1px solid ${(props) => props.theme.colors.primary100};
    }
  }

  .txt-value {
    width: calc(100% - 24px);
    transform: translateY(1px);
  }
  .ic-clear {
    transition: all 0.3s;
    color: ${(props) => getAlphaColor(props.theme.colors.gray800, 0.25)};
    &:hover {
      color: ${(props) => getAlphaColor(props.theme.colors.gray800, 0.5)};
    }
  }
`;

export const SelectedItemStyle = styled(Flex)`
  gap: 6px;
  font-size: 14px;
  align-items: center;
  min-height: 24px;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s;
  color: ${(props) => props.theme.colors.gray700};
  background: ${(props) => props.theme.colors.ND04};
  width: fit-content;
  &:hover {
    background: ${(props) => props.theme.colors.ND08};
  }
`;
